import { useEffect, useMemo, useState } from 'react';
import { toast } from 'material-react-toastify';
import { Box, Button, IconButton, Typography } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DeleteIcon from '@mui/icons-material/Delete';
import { Product } from '../../../interfaces/Product/Product';
import { deleteProduct, getProduct, updateProduct } from '../../../api/Product';
import { sharedStyles } from '../../../utils/Style';
import { Waiting } from '../../../components/Waiting/Waiting';
import { ModifyProductFields } from '../ModifyProductFields';
import ConfirmDialog from '../../../components/ConfirmDialog/ConfirmDialog';
import { UpdateProductTabProps } from '../../../interfaces/Product/UpdateProductTab';
import { OpenInFrontend } from '../../../components/OpenInFrontend/OpenInFrontend';

export const UpdateProductDetails = (props: UpdateProductTabProps) => {
  const location = useLocation();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [clickedDelete, setClickedDelete] = useState(false);
  const [product, setProduct] = useState<Product | undefined>(undefined);
  const [isActive, setIsActive] = useState(false);

  const productAsStr = useMemo(() => JSON.stringify(product), [product]);

  const refreshProduct = () => {
    setLoading(true);
    getProduct(props.productID)
      .then((p) => {
        setProduct(p);
        setIsActive(p.isActive);
      })
      .catch((err) => toast.error(err.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    refreshProduct();
  }, []);

  const handleSave = (newProduct: Product) => {
    setLoading(true);
    updateProduct(newProduct)
      .then((p) => {
        toast.success('Ürün başarıyla güncellendi');
        setProduct(p);
        setIsActive(p.isActive);
      })
      .catch((err) => toast.error(err.message))
      .finally(() => setLoading(false));
  };

  const handleDelete = () => {
    setLoading(true);
    deleteProduct(props.productID)
      .then(() => {
        toast.success('Ürün başarıyla silindi');
        goBack();
      })
      .catch((err) => toast.error(err.message))
      .finally(() => setLoading(false));
  };

  const goBack = () => navigate(`/products/catalog${location.search}`);

  return (
    <Box sx={{ ...sharedStyles.pageBody, p: 3 }}>
      {clickedDelete && (
        <ConfirmDialog
          content='Gerçekten seçili ürünü silmek istiyor musunuz'
          open={clickedDelete}
          onClose={() => setClickedDelete(false)}
          onConfirm={handleDelete}
          color='error'
        />
      )}
      <Waiting open={loading} />
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          flexWrap: 'wrap',
          mb: 3,
          gap: 1.5,
        }}
      >
        <IconButton onClick={goBack} sx={{ ...sharedStyles.backIcon }}>
          <ArrowBackIcon />
        </IconButton>

        <Typography variant='h5'>Ürün Bilgileri</Typography>

        <Box sx={{ flexGrow: 1 }} />

        {!!product && (
          <OpenInFrontend path='urun' slug={product.slug} isActive={isActive} />
        )}

        <Button
          variant='contained'
          color='error'
          size='small'
          startIcon={<DeleteIcon />}
          onClick={() => setClickedDelete(true)}
          sx={{ ...sharedStyles.buttonText }}
        >
          Sil
        </Button>
      </Box>
      {!!product && (
        <ModifyProductFields
          key={productAsStr}
          initialValue={product}
          onSubmit={handleSave}
        />
      )}
    </Box>
  );
};
